'use client';

import { useEffect, useMemo, createContext, useState } from "react";
import { setIsFlutterWebview, setLocale } from "@/store/reducers/appSlice";
import { useDispatch, useSelector } from "react-redux";
import { UI_LOCALE_LANGUAGES } from "@/lib/constants";
import { isBlank } from "@/lib/utils";
import { getCurrentBearerToken } from "@/lib/client-auth";

declare global {
    interface Window {
        isFlutterWebview: boolean;
        flutterLocale: string | null;
        flutterAuth: {
            setToken: (token: string) => void;
            clearToken: () => void;
        }
    }
}

export type MobileWebviewContextType = {
    isFlutterWebview: boolean;
    flutterLocale: string | null;
};

export const MobileWebviewContext = createContext<MobileWebviewContextType>({ isFlutterWebview: false, flutterLocale: null });

function flutterLocaleToAppLocale(flutterLocale: string | null): string | null {
    if (flutterLocale == 'en' || flutterLocale.startsWith('en_')) {
        return 'english';
    } else if (flutterLocale == 'es' || flutterLocale.startsWith('es_')) {
        return 'spanish';
    } else if (flutterLocale == 'ru' || flutterLocale.startsWith('ru_')) {
        return 'russian';
    } else if (flutterLocale == 'ar' || flutterLocale.startsWith('ar_')) {
        return 'arabic';
    } else if (flutterLocale == 'zh' || flutterLocale.startsWith('zh_')) {
        return 'chinese';
    } else if (flutterLocale == 'null' || flutterLocale == 'undefined' || flutterLocale == null) {
        console.warn("Flutter Locale is null, undefined, or null, reverting to English");
        return 'english';    
    }
    
    console.warn("Unknown Flutter Locale:", flutterLocale, "reverting to English");
    return 'english';  
}

export function MobileWebviewProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();
    const isFlutterWebview = useSelector((state: any) => (typeof window !== 'undefined' && window.isFlutterWebview) || state.app.isFlutterWebview);
    const storeIsFlutterWebview = useSelector((state: any) => state.app.isFlutterWebview);
    const [flutterLocale, setFlutterLocale] = useState<string | null>(null);
    const currentUser = useSelector((state: any) => state.app.currentUser);

    useEffect(() => {
        if (!storeIsFlutterWebview && window.isFlutterWebview) {
          dispatch(setIsFlutterWebview(true));                            
        }

        // Only set the locale from Flutter, if a value exists
        if (!isBlank(window.flutterLocale)) {
            const appLocaleFromFlutter = flutterLocaleToAppLocale(window.flutterLocale);
            setFlutterLocale(appLocaleFromFlutter as string);
            dispatch(setLocale(appLocaleFromFlutter as keyof typeof UI_LOCALE_LANGUAGES));
        }
    }, [isFlutterWebview]);

    const contextValue: MobileWebviewContextType = useMemo(() => {
        return {
            isFlutterWebview,
            flutterLocale,
        };
    }, [isFlutterWebview]);

    // Refresh Flutter container auth token when user changes
    useEffect(() => {
        if (currentUser != null) {
            window.flutterAuth?.setToken(getCurrentBearerToken());
        } else {
            window.flutterAuth?.clearToken();
        }
    }, [currentUser]);
    
    return (
        <MobileWebviewContext.Provider value={contextValue}>
            {children}
        </MobileWebviewContext.Provider>
    );
}

export default MobileWebviewProvider;